<template>
  <v-navigation-drawer
    app
    clipped
    ref="navigation"
    v-model="drawer"
    :permanent="$vuetify.breakpoint.mdAndUp"
    :absolute="$vuetify.breakpoint.smAndDown"
    :temporary="$vuetify.breakpoint.smAndDown"
    :mini-variant.sync="showMini"
    :mini-variant-width="60"
  >
    <v-list dense :class="{ 'nav-drawer-list': isMobile($vuetify) }">
      <template
        v-for="({ icon, text, to, query, iconClass, ...rest }, i) in navItems"
      >
        <v-list-item
          link
          exact-path
          :key="`${i}_${text}`"
          :to="{ path: to, query }"
          @click="selectedLink = to"
          class="nav--link subtitle-2"
          active-class="nav--link-active"
          :class="[rest.class, drawer ? 'pr-4' : 'pr-1']"
        >
          <tooltip
            #default="{ on, attrs }"
            :name="text"
            :disabled="!isDrawerOpen"
            position="right"
          >
            <v-list-item-icon
              v-on="on"
              v-bind="attrs"
              class="text--secondary"
              :class="[{ 'mr-4': !drawer }, iconClass]"
              style="margin-top: 14px"
            >
              <v-icon v-text="icon" class="text--secondary" />
            </v-list-item-icon>
          </tooltip>
          <v-list-item-content>
            <v-list-item-title
              :class="{
                'pr-1 text--secondary': !drawer && selectedLink !== to,
              }"
              class="
                subtitle-2
                text-uppercase
                font-weight-bold
                secondary--font
                overflow-visible
              "
            >
              {{ text }}
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import { Tooltip } from "@/components/shared";
import { isMobile } from "@/utils/common.utils";

/**
 * Left side bar nav drawer
 */
export default {
  name: "NavDrawer",
  /**
   * -------------- Props -------------
   */
  props: {
    isDrawerOpen: { type: Boolean, required: true, default: false },
  },
  /**
   * -------------- Components -------------
   */
  components: {
    Tooltip,
  },
  /**
   * Custom events emitted by the component
   */
  emits: ["toggle-drawer"],
  /**
   * -------------- Data Properties -------------
   */
  data() {
    return {
      selectedLink: "",
    };
  },
  computed: {
    /**
     * Shows left side drawer icons when the drawer is closed
     * @description it should be opposite of drawer prop
     */
    showMini: {
      get() {
        return this.$vuetify.breakpoint.smAndDown ? false : this.drawer;
      },
      set() {
        // Commented to not open the drawer on nav link click
        // this.toggleDrawer();
      },
    },
    /**
     * Computed property to toggle the drawer
     */
    drawer: {
      get() {
        return this.isDrawerOpen;
      },
      /**
       * Toggles nav drawer
       */
      set() {
        // call toggleDrawer only when menu inactive
        const { isBooted, isActive } = this.$refs.navigation;
        if (!isBooted || !isActive) this.toggleDrawer();
      },
    },
    /**
     * Left side drawer nav items
     */
    navItems() {
      return [
        /**
         * @todo uncomment dashboard object to display the dasboard nav in the drawer
         */
        // {
        //   icon: "mdi-home",
        //   text: "Dashboard",
        //   to: this.$paths.dashboard,
        // },
        {
          icon: "business_center",
          text: "Customers",
          to: this.$paths.customers,
          iconClass: "material-icons d-flex",
        },
        {
          to: this.$paths.accounts,
          text: "Accounts",
          icon: "place",
          iconClass: "material-icons d-flex",
        },
        {
          icon: "people",
          text: "Users",
          to: this.$paths.users,
          iconClass: "material-icons d-flex",
        },
        {
          to: this.$paths.mailboxProviders,
          text: "Mailbox providers",
          icon: "markunread_mailbox",
        },
        {
          to: this.$paths.errors,
          text: "Error Logs",
          icon: "error",
          iconClass: "material-icons d-flex",
        },
      ];
    },
  },
  /**
   * -------------- Methods -------------
   */
  methods: {
    isMobile,
    /**
     * Emits an event to toggle the left side nav drawer
     */
    toggleDrawer() {
      this.$emit("toggle-drawer");
    },
  },
  /**
   * -------------- Created lifecycle hook -------------
   */
  created() {
    this.selectedLink = this.$route?.path;
  },
};
</script>

<style lang="scss" scoped>
// Left side bar nav links
.nav--link.v-list-item:focus {
  &::before {
    color: var(--v-primary-base) !important;
  }
}

.v-list {
  .nav--link {
    min-height: 52px !important;

    &.nav--link-active {
      .v-list-item__content,
      .v-icon {
        color: var(--v-primary-base) !important;
      }
    }

    &.theme--light.v-list-item:hover::before {
      background-color: var(--v-primary-base);
    }

    &.v-list-item--active {
      color: var(--v-primary-base) !important;
      border-left: 4px solid var(--v-primary-base);
      padding-right: 22px;
      padding-left: 12px;
      .v-icon {
        color: var(--v-primary-base) !important;
      }
    }
  }
}
.nav-drawer-list {
  padding-top: calc(50% - 64px);
}
</style>
